import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import {
  CalendarDays, Clock, Users, User, Phone, Mail, ChevronLeft,
  CheckCircle, Baby, GraduationCap, ChevronDown
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_e26bd618-434d-4121-947e-65674a4d52da/artifacts/twhikqt6_padel%20logo%20png%202.png";

const PERSONAL_PRICES = { 1: 250, 2: 225, 3: 200, 4: 175 };
const STELLAR_PRICES = { 1: 300, 2: 275, 3: 250, 4: 225 };
const STELLAR_MONTHLY = { 1: 275, 2: 250, 3: 225, 4: 200 };
const MONTHLY_PRICES = { 1: 225, 2: 200, 3: 175, 4: 150 };
const STELLAR_SLOTS = ["5:00 PM - 6:30 PM", "6:30 PM - 8:00 PM", "8:00 PM - 9:30 PM", "9:30 PM - 11:00 PM"];

export default function ClassesPage() {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") === "clinic" ? "clinic" : "personal");
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [numParticipants, setNumParticipants] = useState(1);
  const [participants, setParticipants] = useState([{ name: "", phone: "", email: "" }]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Clinic state
  const [clinicInfo, setClinicInfo] = useState(null);
  const [childName, setChildName] = useState("");
  const [childAge, setChildAge] = useState("");
  const [parentName, setParentName] = useState("");
  const [parentPhone, setParentPhone] = useState("");
  const [parentEmail, setParentEmail] = useState("");

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const disabledDays = [{ dayOfWeek: [0] }, { before: today }];
  // Clinics only Mon-Thu
  const clinicDisabledDays = [{ dayOfWeek: [0, 5, 6] }, { before: today }];

  const fetchSlots = useCallback(async (date) => {
    setLoadingSlots(true);
    setSelectedSlot(null);
    try {
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
      const { data } = await axios.get(`${API}/classes/available-slots?date=${dateStr}`);
      // Filter past slots if today
      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();
      const filtered = (data.slots || []).map(slot => {
        if (isToday) {
          const startTime = slot.time.split(" - ")[0];
          const [time, period] = startTime.split(" ");
          const [hours, minutes] = time.split(":").map(Number);
          let h = hours;
          if (period === "PM" && hours !== 12) h += 12;
          if (period === "AM" && hours === 12) h = 0;
          const slotDate = new Date(date);
          slotDate.setHours(h, minutes, 0, 0);
          if (slotDate <= now) return { ...slot, fully_booked: true, passed: true };
        }
        return slot;
      });
      setSlots(filtered);
    } catch { setSlots([]); }
    finally { setLoadingSlots(false); }
  }, []);

  const fetchClinicInfo = useCallback(async (date) => {
    try {
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
      const { data } = await axios.get(`${API}/classes/clinic-availability?date=${dateStr}`);
      setClinicInfo(data);
    } catch { setClinicInfo(null); }
  }, []);

  useEffect(() => {
    if (selectedDate && activeTab === "personal") fetchSlots(selectedDate);
    if (selectedDate && activeTab === "clinic") fetchClinicInfo(selectedDate);
  }, [selectedDate, activeTab, fetchSlots, fetchClinicInfo]);

  const updateParticipantCount = (num) => {
    setNumParticipants(num);
    const newP = Array.from({ length: num }, (_, i) => participants[i] || { name: "", phone: "", email: "" });
    setParticipants(newP);
  };

  const updateParticipant = (index, field, value) => {
    const updated = [...participants];
    updated[index] = { ...updated[index], [field]: value };
    setParticipants(updated);
  };

  const handlePersonalSubmit = async () => {
    if (!selectedDate || !selectedSlot) {
      toast.error("Selecciona fecha y horario");
      return;
    }
    if (participants.some(p => !p.name.trim() || !p.phone.trim())) {
      toast.error("Completa nombre y teléfono de todos los participantes");
      return;
    }
    setSubmitting(true);
    try {
      const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`;
      await axios.post(`${API}/classes/personal`, {
        date: dateStr,
        time_slot: selectedSlot,
        num_participants: numParticipants,
        participants,
      });
      setSuccess(true);
      toast.success("Clase reservada exitosamente");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al reservar");
    } finally { setSubmitting(false); }
  };

  const handleClinicSubmit = async () => {
    if (!selectedDate || !childName || !childAge || !parentName || !parentPhone) {
      toast.error("Completa todos los campos obligatorios");
      return;
    }
    setSubmitting(true);
    try {
      const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`;
      await axios.post(`${API}/classes/clinic`, {
        date: dateStr,
        child_name: childName,
        child_age: parseInt(childAge),
        parent_name: parentName,
        parent_phone: parentPhone,
        parent_email: parentEmail,
      });
      setSuccess(true);
      toast.success("Inscripción registrada exitosamente");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al inscribir");
    } finally { setSubmitting(false); }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-4">
        <div className="text-center max-w-md fade-in">
          <img src={LOGO_URL} alt="PCDR" className="h-20 mx-auto mb-8" />
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-50 mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-zinc-950 mb-4" data-testid="class-success-title">
            {activeTab === "personal" ? "Clase Reservada" : "Inscripción Registrada"}
          </h1>
          <p className="text-zinc-600 mb-4">
            {activeTab === "personal"
              ? "Tu clase con el profesor Diego González ha sido reservada. El pago se realiza directamente en el establecimiento."
              : "La inscripción a la clínica ha sido registrada. El pago se realiza directamente en el establecimiento."}
          </p>
          <p className="text-sm text-zinc-500 mb-8">Recuerda llegar 10 minutos antes.</p>
          <div className="flex gap-3 justify-center">
            <Link to="/">
              <Button variant="outline" data-testid="back-home-btn">Volver al Inicio</Button>
            </Link>
            <Button onClick={() => { setSuccess(false); setSelectedDate(null); setSelectedSlot(null); }} className="bg-zinc-950 text-white" data-testid="new-class-btn">
              Reservar Otra Clase
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const isStellarSlot = selectedSlot && STELLAR_SLOTS.includes(selectedSlot);
  const pricePerPerson = isStellarSlot ? STELLAR_PRICES[numParticipants] : PERSONAL_PRICES[numParticipants];

  return (
    <div className="min-h-screen bg-white">
      <Toaster position="top-center" />

      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="back-link">
            <ChevronLeft className="w-5 h-5" /> Volver
          </Link>
          <img src={LOGO_URL} alt="PCDR" className="h-14 sm:h-16" />
          <div className="w-20" />
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-10 text-center">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-zinc-950 mb-3" data-testid="classes-title">
          Clases de Padel
        </h1>
        <p className="text-base text-zinc-600 mb-2">Profesor: <strong>Diego González</strong></p>
        <p className="text-sm text-zinc-500 max-w-xl mx-auto">
          Reserva tu clase personalizada o inscribe a tus hijos en nuestras clínicas. El pago se realiza directamente en el establecimiento.
        </p>
        <div className="mt-4 animate-bounce" data-testid="scroll-indicator">
          <ChevronDown className="w-6 h-6 text-zinc-400 mx-auto" />
          <p className="text-xs text-zinc-400">Desliza para ver horarios</p>
        </div>
      </section>

      {/* Tabs */}
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex gap-1 border-b border-zinc-200 mb-8">
          <button
            onClick={() => { setActiveTab("personal"); setSelectedDate(null); setSelectedSlot(null); }}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "personal" ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-700"
            }`}
            data-testid="tab-personal"
          >
            <User className="w-4 h-4 inline mr-1.5 -mt-0.5" /> Clase Personalizada
          </button>
          <button
            onClick={() => { setActiveTab("clinic"); setSelectedDate(null); setClinicInfo(null); }}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "clinic" ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-700"
            }`}
            data-testid="tab-clinic"
          >
            <Baby className="w-4 h-4 inline mr-1.5 -mt-0.5" /> Clínica de Niños
          </button>
        </div>
      </div>

      {/* PERSONAL CLASS TAB */}
      {activeTab === "personal" && (
        <section className="max-w-4xl mx-auto px-4 pb-16">
          {/* Pricing tables */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {/* Regular hours pricing */}
            <Card className="border border-zinc-200 shadow-none" data-testid="pricing-card-regular">
              <CardContent className="p-5">
                <h3 className="font-semibold text-zinc-950 mb-1">Horarios Regulares</h3>
                <p className="text-xs text-zinc-500 mb-3">6:00 AM - 5:00 PM (incluye cancha)</p>
                <div className="grid grid-cols-2 gap-2">
                  {[1, 2, 3, 4].map(n => (
                    <button key={n} onClick={() => updateParticipantCount(n)} className={`p-2 border rounded-md text-center ${numParticipants === n ? "border-2 border-zinc-950 bg-zinc-50" : "border-zinc-200"}`} data-testid={`select-${n}-person`}>
                      <p className="text-xs text-zinc-500">{n} {n === 1 ? "persona" : "personas"}</p>
                      <p className="text-lg font-bold text-zinc-950">${PERSONAL_PRICES[n]}</p>
                    </button>
                  ))}
                </div>
                <div className="mt-3 bg-zinc-950 text-white rounded-md p-3 text-center">
                  <p className="text-xs font-bold mb-1">PAQUETE MENSUAL (4 clases)</p>
                  <div className="grid grid-cols-2 gap-1 text-xs mt-1">
                    {[1, 2, 3, 4].map(n => (
                      <div key={n}><span className="text-zinc-400">{n}p: </span><span className="font-bold">${MONTHLY_PRICES[n]}</span></div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stellar hours pricing */}
            <Card className="border-2 border-amber-400 shadow-none bg-amber-50/30" data-testid="pricing-card-stellar">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-zinc-950">Horarios Estelares</h3>
                  <span className="text-xs bg-amber-400 text-amber-900 px-2 py-0.5 rounded font-bold">+$50</span>
                </div>
                <p className="text-xs text-zinc-500 mb-3">5:00 PM - 11:00 PM (incluye cancha)</p>
                <div className="grid grid-cols-2 gap-2">
                  {[1, 2, 3, 4].map(n => (
                    <button key={n} onClick={() => updateParticipantCount(n)} className={`p-2 border rounded-md text-center ${numParticipants === n ? "border-2 border-amber-500 bg-amber-50" : "border-amber-200"}`} data-testid={`select-stellar-${n}-person`}>
                      <p className="text-xs text-zinc-500">{n} {n === 1 ? "persona" : "personas"}</p>
                      <p className="text-lg font-bold text-zinc-950">${STELLAR_PRICES[n]}</p>
                    </button>
                  ))}
                </div>
                <div className="mt-3 bg-amber-500 text-white rounded-md p-3 text-center">
                  <p className="text-xs font-bold mb-1">PAQUETE MENSUAL (4 clases)</p>
                  <div className="grid grid-cols-2 gap-1 text-xs mt-1">
                    {[1, 2, 3, 4].map(n => (
                      <div key={n}><span className="text-amber-100">{n}p: </span><span className="font-bold">${STELLAR_MONTHLY[n]}</span></div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Calendar */}
            <Card className="border border-zinc-200 shadow-none" data-testid="personal-calendar-card">
              <CardContent className="p-6">
                <h3 className="font-medium text-zinc-800 mb-3 text-sm">Selecciona fecha</h3>
                <div className="flex justify-center">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={disabledDays}
                    fromDate={today}
                    toDate={new Date(2030, 11, 31)}
                    className="rounded-md"
                    data-testid="personal-calendar"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Slots */}
            <Card className="border border-zinc-200 shadow-none" data-testid="personal-slots-card">
              <CardContent className="p-6">
                {!selectedDate ? (
                  <div className="flex flex-col items-center justify-center h-full text-zinc-400 py-12">
                    <CalendarDays className="w-12 h-12 mb-4" />
                    <p className="text-sm">Selecciona una fecha</p>
                  </div>
                ) : loadingSlots ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-zinc-950 border-t-transparent" />
                  </div>
                ) : (
                  <>
                    <h3 className="font-medium text-zinc-800 mb-3 text-sm">Horarios disponibles</h3>
                    <div className="grid grid-cols-2 gap-2 max-h-[350px] overflow-y-auto pr-1">
                      {slots.map(slot => (
                        <button
                          key={slot.time}
                          disabled={slot.fully_booked}
                          onClick={() => setSelectedSlot(slot.time)}
                          className={`p-3 border rounded-md text-sm transition-all ${
                            slot.fully_booked
                              ? "opacity-40 cursor-not-allowed bg-zinc-100"
                              : selectedSlot === slot.time
                              ? "border-2 border-zinc-950 bg-zinc-50 font-semibold"
                              : STELLAR_SLOTS.includes(slot.time)
                              ? "border-amber-300 bg-amber-50/50 hover:border-amber-500"
                              : "border-zinc-200 hover:border-zinc-400"
                          }`}
                          data-testid={`class-slot-${slot.time.replace(/[:\s]/g, "-")}`}
                        >
                          <Clock className="w-3.5 h-3.5 inline mr-1 text-zinc-400" />
                          {slot.time}
                          {slot.passed && <span className="text-xs text-red-400 block">Pasado</span>}
                          {slot.clinic_blocked && <span className="text-xs text-blue-500 block">Clínica de niños</span>}
                          {slot.professor_busy && <span className="text-xs text-purple-500 block">Profesor ocupado</span>}
                          {slot.professor_unavailable && <span className="text-xs text-red-400 block">No disponible</span>}
                          {!slot.fully_booked && STELLAR_SLOTS.includes(slot.time) && <span className="text-xs text-amber-600 block font-medium">Horario estelar</span>}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Participant form */}
          {selectedSlot && (
            <Card className="border border-zinc-200 shadow-none mt-8 fade-in" data-testid="participants-form">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-zinc-950">Datos de los Participantes</h3>
                  <div className={`px-3 py-1 rounded text-sm ${isStellarSlot ? "bg-amber-100 text-amber-900" : "bg-zinc-100"}`}>
                    {isStellarSlot && <span className="text-xs font-medium mr-1">Estelar</span>}
                    Total: <strong>${pricePerPerson * numParticipants} MXN</strong>
                  </div>
                </div>
                <div className="space-y-4">
                  {participants.map((p, i) => (
                    <div key={i} className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-3 bg-zinc-50 rounded-md">
                      <div>
                        <label className="text-xs text-zinc-500 flex items-center gap-1 mb-1"><User className="w-3 h-3" /> Nombre *</label>
                        <Input value={p.name} onChange={(e) => updateParticipant(i, "name", e.target.value)} placeholder={`Participante ${i + 1}`} className="text-sm" data-testid={`participant-name-${i}`} />
                      </div>
                      <div>
                        <label className="text-xs text-zinc-500 flex items-center gap-1 mb-1"><Phone className="w-3 h-3" /> Teléfono *</label>
                        <Input value={p.phone} onChange={(e) => updateParticipant(i, "phone", e.target.value)} placeholder="Teléfono" className="text-sm" data-testid={`participant-phone-${i}`} />
                      </div>
                      <div>
                        <label className="text-xs text-zinc-500 flex items-center gap-1 mb-1"><Mail className="w-3 h-3" /> Email</label>
                        <Input value={p.email} onChange={(e) => updateParticipant(i, "email", e.target.value)} placeholder="Opcional" className="text-sm" data-testid={`participant-email-${i}`} />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="bg-zinc-100 border border-zinc-200 rounded-md p-4 mt-4 text-sm text-zinc-600">
                  <p>El pago se realiza directamente en el establecimiento.</p>
                </div>
                <Button onClick={handlePersonalSubmit} disabled={submitting} className="w-full mt-4 bg-zinc-950 text-white hover:bg-zinc-800 py-3" data-testid="reserve-class-btn">
                  {submitting ? "Reservando..." : "Reservar Clase"}
                </Button>
              </CardContent>
            </Card>
          )}
        </section>
      )}

      {/* CLINIC TAB */}
      {activeTab === "clinic" && (
        <section className="max-w-4xl mx-auto px-4 pb-16">
          {/* Clinic info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            <Card className="border border-zinc-200 shadow-none" data-testid="clinic-info-mon">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-blue-50 rounded-md"><Baby className="w-5 h-5 text-blue-600" /></div>
                  <div>
                    <p className="font-semibold text-zinc-950">Lunes y Miércoles</p>
                    <p className="text-sm text-zinc-500">4:00 PM - 6:00 PM</p>
                  </div>
                </div>
                <p className="text-sm text-zinc-600">Edades: <strong>4 - 7 años</strong></p>
                <p className="text-sm text-zinc-600">Cupos: Máximo 10 niños</p>
              </CardContent>
            </Card>
            <Card className="border border-zinc-200 shadow-none" data-testid="clinic-info-tue">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-green-50 rounded-md"><GraduationCap className="w-5 h-5 text-green-600" /></div>
                  <div>
                    <p className="font-semibold text-zinc-950">Martes y Jueves</p>
                    <p className="text-sm text-zinc-500">4:00 PM - 6:00 PM</p>
                  </div>
                </div>
                <p className="text-sm text-zinc-600">Edades: <strong>8 - 12 años</strong></p>
                <p className="text-sm text-zinc-600">Cupos: Máximo 10 niños</p>
              </CardContent>
            </Card>
          </div>
          <div className="max-w-md mx-auto mb-8">
            <Card className="border border-zinc-200 shadow-none" data-testid="clinic-pricing">
              <CardContent className="p-5 text-center">
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div className="p-3 border border-zinc-200 rounded-md">
                    <p className="text-xs text-zinc-500">Por clase</p>
                    <p className="text-2xl font-bold text-zinc-950">$150</p>
                    <p className="text-xs text-zinc-400">MXN</p>
                  </div>
                  <div className="p-3 border-2 border-zinc-950 rounded-md bg-zinc-50">
                    <p className="text-xs text-zinc-500">Mensualidad</p>
                    <p className="text-2xl font-bold text-zinc-950">$789</p>
                    <p className="text-xs text-green-600 font-medium">Ahorra $411/mes</p>
                  </div>
                </div>
                <p className="text-xs text-zinc-500">La mensualidad incluye todas las clases del mes (8 clases aprox.)</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Calendar */}
            <Card className="border border-zinc-200 shadow-none">
              <CardContent className="p-6">
                <h3 className="font-medium text-zinc-800 mb-3 text-sm">Selecciona fecha (Lun-Jue)</h3>
                <div className="flex justify-center">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={clinicDisabledDays}
                    fromDate={today}
                    toDate={new Date(2030, 11, 31)}
                    className="rounded-md"
                    data-testid="clinic-calendar"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Registration form */}
            <Card className="border border-zinc-200 shadow-none">
              <CardContent className="p-6">
                {!selectedDate ? (
                  <div className="flex flex-col items-center justify-center h-full text-zinc-400 py-12">
                    <CalendarDays className="w-12 h-12 mb-4" />
                    <p className="text-sm">Selecciona una fecha</p>
                  </div>
                ) : !clinicInfo?.available ? (
                  <div className="text-center py-12 text-zinc-500">
                    <p>{clinicInfo?.message || "No hay clínica este día"}</p>
                  </div>
                ) : (
                  <>
                    <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3 mb-4">
                      <p className="text-sm font-medium text-zinc-800">{clinicInfo.clinic_type} - {clinicInfo.clinic_days}</p>
                      <p className="text-sm text-zinc-600">{clinicInfo.time_slot}</p>
                      <p className="text-sm text-zinc-600">Cupos disponibles: <strong className={clinicInfo.spots_left <= 2 ? "text-red-600" : "text-green-600"}>{clinicInfo.spots_left}</strong></p>
                    </div>

                    {clinicInfo.spots_left <= 0 ? (
                      <p className="text-center text-red-600 font-medium py-8">No hay cupo disponible</p>
                    ) : (
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs text-zinc-500 mb-1 block">Nombre del niño/a *</label>
                          <Input value={childName} onChange={(e) => setChildName(e.target.value)} placeholder="Nombre del niño/a" className="text-sm" data-testid="child-name" />
                        </div>
                        <div>
                          <label className="text-xs text-zinc-500 mb-1 block">Edad *</label>
                          <Input type="number" value={childAge} onChange={(e) => setChildAge(e.target.value)} placeholder="Edad" min={4} max={12} className="text-sm" data-testid="child-age" />
                        </div>
                        <Separator />
                        <div>
                          <label className="text-xs text-zinc-500 mb-1 block">Nombre del padre/madre *</label>
                          <Input value={parentName} onChange={(e) => setParentName(e.target.value)} placeholder="Nombre completo" className="text-sm" data-testid="parent-name" />
                        </div>
                        <div>
                          <label className="text-xs text-zinc-500 mb-1 block">Teléfono *</label>
                          <Input value={parentPhone} onChange={(e) => setParentPhone(e.target.value)} placeholder="Teléfono" className="text-sm" data-testid="parent-phone" />
                        </div>
                        <div>
                          <label className="text-xs text-zinc-500 mb-1 block">Email (opcional)</label>
                          <Input value={parentEmail} onChange={(e) => setParentEmail(e.target.value)} placeholder="Correo" className="text-sm" data-testid="parent-email" />
                        </div>
                        <div className="bg-zinc-100 border border-zinc-200 rounded-md p-3 text-sm text-zinc-600">
                          <p>Pago en el establecimiento: $150 MXN por clase</p>
                        </div>
                        <Button onClick={handleClinicSubmit} disabled={submitting} className="w-full bg-zinc-950 text-white hover:bg-zinc-800 py-3" data-testid="register-clinic-btn">
                          {submitting ? "Inscribiendo..." : "Inscribir"}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="border-t border-zinc-200 py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <Link to="/" className="text-sm text-zinc-400 hover:text-zinc-600 transition-colors">Volver al Inicio</Link>
        </div>
      </footer>
    </div>
  );
}
